// REACT IMPORTS
import { useEffect, useState, useCallback, useRef } from "react";

// COMPONENT IMPORTS
import SidePanel from "components/auth/SidePanel";
import LoginForm from "components/auth/LoginForm";
import SendOtpForm from "components/auth/SendOtpForm";
import OtpVerificationForm from "components/auth/OtpVerificationForm";
import LegalNotice from "components/auth/LegalNotice";
import CookieNotice from "components/auth/CookieNotice";
import { useAppDispatch } from "store/hooks";
import {
  onAcceptCookies,
  onCloseWarn,
  useIsLogin,
} from "store/slices/commonSlice";
import Drawer from "components/ui-component/SideDrawer";
import Preferences from "components/auth/Preferences";
import { useCookies } from "react-cookie";
import moment from "moment";
import {
  accountDetailsGet,
  updateToken,
  updateUserData,
  useAccountDetails,
  useUserData,
} from "store/slices/authSlice";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useParams } from "react-router-dom";
import LoginViaCredForm from "components/auth/LoginViaCredForm";
import { CloseIcon } from "icons/close/CloseIcon";
import CustomModal from "components/ui-component/CustomModal";
import Skeleton from "react-loading-skeleton";
import Helment from "components/layout/helmet";
import {
  portalSettingsGet,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { companyMarkersGet } from "store/slices/paymentsSlice";
import { decode, shouldBypassLegalNotice } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import { SettingsValue } from "types/payments";
import { TestIdScope } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- LOGIN PAGE ------------------------------------- */

const Login = ({
  guestLogin = false,
  setShowOverlay = () => {},
}: {
  guestLogin?: boolean;
  setShowOverlay?: any;
}) => {
  const dispatch = useAppDispatch();
  const isLogin = useIsLogin();
  const portalSettings = usePortalSettingsData();
  const [openPreferences, setOpenPreferences] = useState<boolean>(false);
  // Height of the fixed cookie banner, kept clear below the form column so the
  // Accept and Login buttons can scroll out from under it.
  const [cookieNoticeHeight, setCookieNoticeHeight] = useState<number>(0);
  const [cookies, setCookie] = useCookies(["acceptCookies", "acceptLegalForm"]);
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const [showLegalForm, setShowLegalForm] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(true);
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });

  const handleAccept = (value: boolean) => {
    setCookie("acceptLegalForm", value, {
      expires: moment().add(3, "months").toDate(),
    });
    setShowLegalForm(false);
  };

  const accountDetails = useAccountDetails();

  // Guest login on a link page, on a cluster with two-factor login: the
  // overlay walks through "send code" and "enter code" itself, because the
  // token from the first step is not a session until the code is verified.
  const userData = useUserData();
  const [guestStep, setGuestStep] = useState<"login" | "send" | "verify">(
    "login"
  );
  const [otpDestination, setOtpDestination] = useState<any>(null);
  // The link's own account details, as they were before anyone logged in.
  const accountBeforeLogin = useRef<any>(userData?.data?.accountData);
  const guestClusterId =
    decode(id || "")?.split("_")?.[1] || clusterId || "";

  // Leaving the code steps half way: drop the unfinished login and put the
  // page's own account details back.
  const cancelGuestOtp = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_TIME);
    dispatch(updateToken({ token: null }));
    dispatch(updateUserData(accountBeforeLogin.current));
    setOtpDestination(null);
    setGuestStep("login");
  };

  const closeGuestLogin = () => {
    if (guestStep !== "login") {
      cancelGuestOtp();
    }
    setShowOverlay(false);
  };

  const onGetPortalSettings = useCallback(
    async (payload: any) => {
      try {
        let res: any = await dispatch(portalSettingsGet(payload));
        await dispatch(companyMarkersGet(payload));

        if (res?.payload?.body?.success) {
          if (
            res?.payload?.body?.data?.min_marinda_flag !==
              SettingsValue.CUSTOM &&
            res?.payload?.body?.data?.min_marinda_flag !== SettingsValue.DEFAULT
          ) {
            dispatch(onCloseWarn(false));
          }
        }
      } catch (error) {
        console.log("error =====", error);
      } finally {
        setIsScreenLoading(false);
      }
    },
    [dispatch]
  );

  const onGetAccountDetails = async () => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        // a link page carries the cluster inside its link code; quick
        // checkout has it in the address instead
        cluster_id: guestLogin ? slug[1] || clusterId : clusterId,
      };
      onGetPortalSettings(payload);
      await dispatch(accountDetailsGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get account details err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    // Treat a missing auth token as "not logged in" even if the isLogin flag is
    // still stale (e.g. a session that ended mid-navigation). Without this, the
    // login page skips loading the auth form and renders a blank panel.
    const hasActiveSession = !!localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!isLogin || !hasActiveSession) {
      onGetAccountDetails();
    } else {
      setIsLoading(false);
      setIsScreenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  const shouldShowLoginForm =
    accountDetails?.data?.login_type === "CD" ||
    accountDetails?.data?.login_type === "BOTH";

  const shouldShowCredForm = accountDetails?.data?.login_type === "EP";

  const showLegalNoticeForm =
    portalSettings?.legal_notice_option === SettingsValue.CUSTOM ||
    portalSettings?.legal_notice_option === SettingsValue.DEFAULT;

  const isLegalNoticeAccepted = cookies?.acceptLegalForm || !showLegalForm;

  const shouldShowLoginDirectly = shouldBypassLegalNotice(
    portalSettings?.legal_notice_option
  );

  const renderLoginForm = () => {
    if (guestLogin && guestStep === "send") {
      return (
        <SendOtpForm
          guest={{
            clusterId: guestClusterId,
            onSent: (destination: any) => {
              setOtpDestination(destination);
              setGuestStep("verify");
            },
            onCancel: cancelGuestOtp,
          }}
        />
      );
    }
    if (guestLogin && guestStep === "verify") {
      return (
        <OtpVerificationForm
          guest={{
            destination: otpDestination,
            onVerified: () => setShowOverlay(false),
            onCancel: cancelGuestOtp,
          }}
        />
      );
    }
    const onOtpRequired = guestLogin ? () => setGuestStep("send") : undefined;
    if (shouldShowLoginForm) {
      return (
        <LoginForm
          guestLogin={guestLogin}
          setShowOverlay={setShowOverlay}
          onOtpRequired={onOtpRequired}
        />
      );
    } else if (shouldShowCredForm) {
      return (
        <LoginViaCredForm
          guestLogin={guestLogin}
          setShowOverlay={setShowOverlay}
          onOtpRequired={onOtpRequired}
        />
      );
    }
    return <></>;
  };

  return (
    <>
      <Helment />
      <TestIdScope scope={guestLogin ? "guest-login" : "auth-login"}>
        <div
          data-testid={guestLogin ? "guest-login-page" : "auth-login-page"}
          className={`portal-shell h-[var(--shell-vh)] flex flex-col lg:flex-row lg:justify-center lg:overflow-y-none overflow-y-auto relative ${
            !guestLogin && "animate__animated animate__fadeIn"
          }`}
        >
          {guestLogin && (
            <div
              className="absolute top-8 right-8 cursor-pointer"
              onClick={closeGuestLogin}
              onKeyDown={activateOnKey(closeGuestLogin)}
              role="button"
              tabIndex={0}
              aria-label="Close login"
            >
              <CloseIcon />
            </div>
          )}
          {isScreenLoading ? (
            <div className="h-full w-full grid place-items-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            </div>
          ) : (
            <>
              <div className="lg:w-2/5 w-full">
                <SidePanel />
              </div>
              {/* On desktop the design starts the form 150px into the 1170px panel
                  (the shell is scaled to that canvas) rather than centring it; mobile stays centred. */}
              <div
                className="lg:w-3/5 h-full flex flex-col justify-between items-center lg:items-start lg:pr-0 lg:overflow-y-auto px-4 overflow-y-scroll"
                // End the scroll area where the cookie banner starts. The margin
                // shrinks the column when stacked on phones; the max-height
                // does it beside the side panel on desktop.
                style={
                  cookieNoticeHeight
                    ? {
                        marginBottom: cookieNoticeHeight,
                        maxHeight: `calc(100% - ${cookieNoticeHeight}px)`,
                      }
                    : undefined
                }
              >
                <div className="lg:pt-[134px] pt-4 h-full lg:mx-auto">
                  {isLoading ? (
                    <>
                      <div className="lg:block hidden">
                        <Skeleton width={520} height={36} />
                        <div className="py-10">
                          <Skeleton width={520} height={68} />
                        </div>
                        <Skeleton width={160} height={26} />
                        <Skeleton width={520} height={61} />
                        <div className="pt-[60px]">
                          <Skeleton width={520} height={44} />
                        </div>
                      </div>
                      <div className="lg:hidden w-full block">
                        <Skeleton width={393} height={30} />
                        <div className="pb-10 pt-6">
                          <Skeleton width={393} height={65} />
                        </div>
                        <Skeleton width={114} height={18} />
                        <Skeleton width={393} height={47} />
                        <div className="pt-[60px]">
                          <Skeleton width={393} height={36} />
                        </div>
                      </div>
                    </>
                  ) : shouldShowLoginDirectly ? (
                    renderLoginForm()
                  ) : showLegalNoticeForm ? (
                    isLegalNoticeAccepted ? (
                      renderLoginForm()
                    ) : (
                      <LegalNotice onClick={() => handleAccept(true)} />
                    )
                  ) : null}
                </div>
              </div>

              {portalSettings?.data_privacy === SettingsValue.NO ||
              isLoading ? null : (cookies.acceptCookies &&
                  (cookies.acceptCookies === "acceptCookies" ||
                    cookies.acceptCookies === "do_not_sell")) ||
                guestLogin ? null : (
                <div className="fixed bottom-0">
                  <CookieNotice
                    onAccept={() => {
                      dispatch(onAcceptCookies(false));
                    }}
                    onPreferencesClick={() => {
                      setOpenPreferences(true);
                    }}
                    onHeightChange={setCookieNoticeHeight}
                  />
                </div>
              )}
            </>
          )}

          <Drawer
            data-testid="auth-login-privacy-preferences-drawer"
            enableOverlay={true}
            lockBackgroundScroll={true}
            direction="right"
            open={!isLessThan590 ? openPreferences : false}
            size={isLessThan590 ? `100vw` : "590px"}
            onClose={() => {
              setOpenPreferences(false);
            }}
            overlayColor="#000"
          >
            <Preferences setOpenPreferences={setOpenPreferences} />
          </Drawer>

          <CustomModal
            signModal={true}
            isOpen={isLessThan590 ? openPreferences : false}
            className="w-full h-fit rounded-[10px]"
            data-testid="auth-login-privacy-preferences-modal"
            aria-label="Privacy preferences"
            onCloseClick={() => {
              setOpenPreferences(false);
            }}
            isPreferences={true}
            children={<Preferences setOpenPreferences={setOpenPreferences} />}
          />
        </div>
      </TestIdScope>
    </>
  );
};

export default Login;
